import { siteDay } from "@/lib/site-time";
/* i18n-ignore: interne adminpagina, alleen NL */
import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { AdminNav } from "@/components/admin/AdminNav";
import { MediaDialog } from "@/components/admin/MediaDialog";
import { adminProjectQuery } from "@/lib/projects-queries";
import { adminMediaQuery } from "@/lib/media-queries";
import { updateMedia, type MediaAsset } from "@/lib/media.functions";
import {
  deleteProject,
  saveProject,
  saveProjectQuote,
  setProjectMedia,
  type ProjectFaq,
  type ProjectMediaRole,
} from "@/lib/projects.functions";
import { ProjectAiAssist } from "@/components/admin/ProjectAiAssist";
import { projectTypes, projectTypeLabel, slugify } from "@/content/project-types";

export const Route = createFileRoute("/_authenticated/admin/realisaties/$slug")({
  staticData: { sitemap: false },
  head: () => ({ meta: [{ title: "Realisatie bewerken — Gopro Projects" }, { name: "robots", content: "noindex" }] }),
  component: ProjectEditorPage,
});

type PhotoRow = { mediaId: string; role: ProjectMediaRole; pairedMediaId: string | null; media: MediaAsset };

const roleLabels: Record<ProjectMediaRole, string> = {
  hoofdbeeld: "Hoofdbeeld",
  gewoon: "Gewone foto",
  voor: "Voor",
  na: "Na",
};

function toLines(values: string[]) {
  return values.join("\n");
}
function fromLines(value: string) {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function ProjectEditorPage() {
  const { slug } = Route.useParams();
  const isNew = slug === "nieuw";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: existing, isLoading } = useQuery(adminProjectQuery(slug));
  const [kiezen, setKiezen] = useState(false);

  const [form, setForm] = useState({
    titel: "",
    slug: "",
    typeWerk: "bouwkundige-zwembaden",
    gemeente: "",
    provincie: "Limburg",
    uitvoeringsjaar: "",
    afmetingen: "",
    uitgevoerdeWerken: "",
    gebruikteMaterialen: "",
    partners: "",
    samenvatting: "",
    beschrijving: "",
    antwoordSamenvatting: "",
    uitgelicht: false,
    sortOrder: 0,
    metaTitle: "",
    metaDescription: "",
  });
  const [faqs, setFaqs] = useState<ProjectFaq[]>([]);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [quote, setQuote] = useState({
    citaat: "",
    naam: "",
    gemeente: "",
    toestemming: false,
    toestemmingOp: "",
    zichtbaar: false,
  });

  useEffect(() => {
    if (!existing) return;
    setForm({
      titel: existing.titel,
      slug: existing.slug,
      typeWerk: existing.typeWerk,
      gemeente: existing.gemeente,
      provincie: existing.provincie,
      uitvoeringsjaar: existing.uitvoeringsjaar ? String(existing.uitvoeringsjaar) : "",
      afmetingen: existing.afmetingen,
      uitgevoerdeWerken: toLines(existing.uitgevoerdeWerken),
      gebruikteMaterialen: toLines(existing.gebruikteMaterialen),
      partners: toLines(existing.partners),
      samenvatting: existing.samenvatting,
      beschrijving: existing.beschrijving,
      antwoordSamenvatting: existing.antwoordSamenvatting,
      uitgelicht: existing.uitgelicht,
      sortOrder: existing.sortOrder,
      metaTitle: existing.metaTitle,
      metaDescription: existing.metaDescription,
    });
    setFaqs(existing.faqs);
    setPhotos(
      existing.photos.map((photo) => ({
        mediaId: photo.mediaId,
        role: photo.role,
        pairedMediaId: photo.pairedMediaId,
        media: photo.media,
      })),
    );
    if (existing.quote) {
      setQuote({
        citaat: existing.quote.citaat,
        naam: existing.quote.naam,
        gemeente: existing.quote.gemeente,
        toestemming: existing.quote.toestemming,
        toestemmingOp: existing.quote.toestemmingOp?.slice(0, 10) ?? "",
        zichtbaar: existing.quote.zichtbaar,
      });
    }
  }, [existing]);

  const effectiveSlug = slugTouched ? form.slug : slugify(form.titel);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ["projects"] });
    void queryClient.invalidateQueries({ queryKey: ["media"] });
  }

  const save = useMutation({
    mutationFn: async (status: "concept" | "gepubliceerd") => {
      const result = await saveProject({
        data: {
          ...(existing ? { id: existing.id } : {}),
          slug: effectiveSlug,
          titel: form.titel,
          typeWerk: form.typeWerk,
          gemeente: form.gemeente,
          provincie: form.provincie,
          uitvoeringsjaar: form.uitvoeringsjaar ? Number(form.uitvoeringsjaar) : null,
          afmetingen: form.afmetingen,
          uitgevoerdeWerken: fromLines(form.uitgevoerdeWerken),
          gebruikteMaterialen: fromLines(form.gebruikteMaterialen),
          partners: fromLines(form.partners),
          samenvatting: form.samenvatting,
          beschrijving: form.beschrijving,
          antwoordSamenvatting: form.antwoordSamenvatting,
          status,
          uitgelicht: form.uitgelicht,
          sortOrder: form.sortOrder,
          metaTitle: form.metaTitle,
          metaDescription: form.metaDescription,
          faqs,
        },
      });
      return result.slug;
    },
    onSuccess: (savedSlug, status) => {
      toast.success(status === "gepubliceerd" ? "De realisatie staat online." : "Opgeslagen als concept.");
      refresh();
      if (savedSlug !== slug) void navigate({ to: "/admin/realisaties/$slug", params: { slug: savedSlug } });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Opslaan mislukt."),
  });

  const savePhotos = useMutation({
    mutationFn: async () => {
      if (!existing) throw new Error("Bewaar eerst de gegevens van de realisatie.");
      const missingAlt = photos.filter((photo) => photo.media.alt.trim() === "");
      if (missingAlt.length > 0) {
        throw new Error("Elke foto heeft een beschrijving nodig. Vul die aan bij Foto's in het beheer.");
      }
      await setProjectMedia({
        data: {
          projectId: existing.id,
          items: photos.map((photo) => ({
            mediaId: photo.mediaId,
            role: photo.role,
            pairedMediaId: photo.role === "voor" ? photo.pairedMediaId : null,
          })),
        },
      });
    },
    onSuccess: () => {
      toast.success("De foto's zijn bewaard.");
      refresh();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Foto's bewaren mislukt."),
  });

  const storeQuote = useMutation({
    mutationFn: async () => {
      if (!existing) throw new Error("Bewaar eerst de gegevens van de realisatie.");
      await saveProjectQuote({
        data: {
          projectId: existing.id,
          citaat: quote.citaat,
          naam: quote.naam,
          gemeente: quote.gemeente,
          toestemming: quote.toestemming,
          toestemmingOp: quote.toestemming ? (quote.toestemmingOp || siteDay()) : null,
          zichtbaar: quote.zichtbaar,
        },
      });
    },
    onSuccess: () => {
      toast.success("De quote is bewaard.");
      refresh();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Quote bewaren mislukt."),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!existing) return;
      await deleteProject({ data: { id: existing.id } });
    },
    onSuccess: () => {
      toast.success("De realisatie is verwijderd.");
      refresh();
      void navigate({ to: "/admin/realisaties" });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Verwijderen mislukt."),
  });

  function addPhoto(asset: MediaAsset) {
    setPhotos((current) =>
      current.some((photo) => photo.mediaId === asset.id)
        ? current
        : [
            ...current,
            {
              mediaId: asset.id,
              role: current.length === 0 ? "hoofdbeeld" : "gewoon",
              pairedMediaId: null,
              media: asset,
            },
          ],
    );
  }

  function movePhoto(index: number, direction: -1 | 1) {
    setPhotos((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (!moved) return current;
      next.splice(target, 0, moved);
      return next;
    });
  }

  const dragIndex = useRef<number | null>(null);
  function dropOn(index: number) {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === index) return;
    setPhotos((current) => {
      const next = [...current];
      const [moved] = next.splice(from, 1);
      if (!moved) return current;
      next.splice(index, 0, moved);
      return next;
    });
  }

  async function addByIds(ids: string[]) {
    const collections = ["bibliotheek", "realisaties", "opbouw", "artikels"] as const;
    const assets = (
      await Promise.all(collections.map((collection) => queryClient.fetchQuery(adminMediaQuery(collection))))
    ).flat();
    for (const id of ids) {
      const asset = assets.find((item) => item.id === id);
      if (asset) addPhoto(asset);
    }
    toast.success("Foto's toegevoegd. Klik op Foto's bewaren om de keuze vast te leggen.");
  }

  if (isLoading && !isNew) {
    return (
      <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        <AdminNav />
        <p className="text-sm text-muted-foreground">Bezig met laden…</p>
      </main>
    );
  }

  const naOpties = photos.filter((photo) => photo.role === "na");

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <AdminNav />

      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
            {isNew ? "Nieuwe realisatie" : form.titel || "Realisatie"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {existing
              ? existing.status === "gepubliceerd"
                ? "Status: online"
                : "Status: concept"
              : "Status: nog niet bewaard"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={save.isPending} onClick={() => save.mutate("concept")}>
            Bewaren als concept
          </Button>
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate("gepubliceerd")}>
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Publiceren
          </Button>
        </div>
      </div>

      {/* Gegevens */}
      <Card className="mb-6">
        <CardContent className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">Gegevens</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="titel">Titel</Label>
              <Input
                id="titel"
                value={form.titel}
                onChange={(event) => setForm({ ...form, titel: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="slug">Webadres</Label>
              <Input
                id="slug"
                value={effectiveSlug}
                onChange={(event) => {
                  setSlugTouched(true);
                  setForm({ ...form, slug: slugify(event.target.value) });
                }}
              />
              <p className="mt-1 text-xs text-muted-foreground">/realisaties/{effectiveSlug || "…"}</p>
            </div>
            <div>
              <Label htmlFor="typeWerk">Soort werk</Label>
              <select
                id="typeWerk"
                value={form.typeWerk}
                onChange={(event) => setForm({ ...form, typeWerk: event.target.value })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {projectTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="gemeenteveld">Gemeente</Label>
              <Input
                id="gemeenteveld"
                value={form.gemeente}
                onChange={(event) => setForm({ ...form, gemeente: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="provincie">Provincie</Label>
              <Input
                id="provincie"
                value={form.provincie}
                onChange={(event) => setForm({ ...form, provincie: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="jaar">Uitvoeringsjaar</Label>
              <Input
                id="jaar"
                inputMode="numeric"
                value={form.uitvoeringsjaar}
                onChange={(event) => setForm({ ...form, uitvoeringsjaar: event.target.value.replace(/\D/g, "") })}
              />
            </div>
            <div>
              <Label htmlFor="afmetingen">Afmetingen bad</Label>
              <Input
                id="afmetingen"
                placeholder="10 x 3 m"
                value={form.afmetingen}
                onChange={(event) => setForm({ ...form, afmetingen: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="volgorde">Volgorde</Label>
              <Input
                id="volgorde"
                inputMode="numeric"
                value={String(form.sortOrder)}
                onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value.replace(/\D/g, "")) || 0 })}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.uitgelicht}
              onChange={(event) => setForm({ ...form, uitgelicht: event.target.checked })}
              className="h-4 w-4"
            />
            Uitgelicht op het overzicht
          </label>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="werken">Uitgevoerde werken (één per lijn)</Label>
              <Textarea
                id="werken"
                rows={5}
                value={form.uitgevoerdeWerken}
                onChange={(event) => setForm({ ...form, uitgevoerdeWerken: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="materialen">Gebruikte materialen (één per lijn)</Label>
              <Textarea
                id="materialen"
                rows={5}
                value={form.gebruikteMaterialen}
                onChange={(event) => setForm({ ...form, gebruikteMaterialen: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="partners">Partners (één per lijn)</Label>
              <Textarea
                id="partners"
                rows={5}
                placeholder={"OCEA-rolluik\nAURA-overkapping"}
                value={form.partners}
                onChange={(event) => setForm({ ...form, partners: event.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="samenvatting">Korte samenvatting</Label>
            <Textarea
              id="samenvatting"
              rows={2}
              value={form.samenvatting}
              onChange={(event) => setForm({ ...form, samenvatting: event.target.value })}
            />
          </div>
          <div>
            <Label id="beschrijving-label" htmlFor="beschrijving">Volledige beschrijving</Label>
            <RichTextEditor
              id="beschrijving"
              labelId="beschrijving-label"
              profiel="artikel"
              value={form.beschrijving}
              onChange={(beschrijving) => setForm((prev) => ({ ...prev, beschrijving }))}
            />
          </div>
          <div>
            <Label htmlFor="antwoord">Antwoordklare samenvatting (2 tot 3 zinnen)</Label>
            <Textarea
              id="antwoord"
              rows={3}
              value={form.antwoordSamenvatting}
              onChange={(event) => setForm({ ...form, antwoordSamenvatting: event.target.value })}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Kort en feitelijk: dit antwoord gebruiken zoekmachines en AI-assistenten.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="metaTitle">Meta title</Label>
              <Input
                id="metaTitle"
                value={form.metaTitle}
                onChange={(event) => setForm({ ...form, metaTitle: event.target.value })}
                placeholder="Leeg laten: de titel wordt gebruikt"
              />
            </div>
            <div>
              <Label htmlFor="metaDescription">Meta description</Label>
              <Input
                id="metaDescription"
                value={form.metaDescription}
                onChange={(event) => setForm({ ...form, metaDescription: event.target.value })}
                placeholder="Leeg laten: de samenvatting wordt gebruikt"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI-hulp */}
      <ProjectAiAssist
        invoer={{
          projectId: existing?.id ?? null,
          titel: form.titel,
          typeWerkLabel: projectTypeLabel(form.typeWerk),
          gemeente: form.gemeente,
          provincie: form.provincie,
          uitvoeringsjaar: form.uitvoeringsjaar ? Number(form.uitvoeringsjaar) : null,
          afmetingen: form.afmetingen,
          uitgevoerdeWerken: fromLines(form.uitgevoerdeWerken),
          gebruikteMaterialen: fromLines(form.gebruikteMaterialen),
          partners: fromLines(form.partners),
          samenvatting: form.samenvatting,
          beschrijving: form.beschrijving,
          fotosZonderAlt: photos
            .filter((photo) => photo.media.alt.trim() === "")
            .map((photo) => ({ mediaId: photo.mediaId, bestandsnaam: photo.media.filename })),
        }}
        onTekst={(veld, waarde) => setForm((huidig) => ({ ...huidig, [veld]: waarde }))}
        onFaq={(faq) => setFaqs((huidig) => [...huidig, faq])}
        onAlt={async (mediaId, alt) => {
          await updateMedia({ data: { id: mediaId, alt } });
          setPhotos((huidig) =>
            huidig.map((photo) =>
              photo.mediaId === mediaId ? { ...photo, media: { ...photo.media, alt } } : photo,
            ),
          );
          refresh();
          toast.success("De fotobeschrijving is bewaard.");
        }}
      />

      {/* Veelgestelde vragen */}
      <Card className="mb-6">
        <CardContent className="space-y-4 p-6">
          <div>
            <h2 className="text-lg font-semibold">Veelgestelde vragen</h2>
            <p className="text-sm text-muted-foreground">
              Deze vragen komen op de detailpagina en in de FAQPage-markup. Ze worden bewaard samen met de gegevens.
            </p>
          </div>
          {faqs.length === 0 && <p className="text-sm text-muted-foreground">Er staan nog geen vragen bij.</p>}
          <ol className="space-y-4">
            {faqs.map((faq, index) => (
              <li key={index} className="rounded-lg border border-border p-3">
                <Label htmlFor={`faq-vraag-${index}`}>Vraag {index + 1}</Label>
                <Input
                  id={`faq-vraag-${index}`}
                  value={faq.vraag}
                  onChange={(event) =>
                    setFaqs((huidig) =>
                      huidig.map((item, i) => (i === index ? { ...item, vraag: event.target.value } : item)),
                    )
                  }
                />
                <Label htmlFor={`faq-antwoord-${index}`} className="mt-3 block">
                  Antwoord
                </Label>
                <Textarea
                  id={`faq-antwoord-${index}`}
                  rows={3}
                  value={faq.antwoord}
                  onChange={(event) =>
                    setFaqs((huidig) =>
                      huidig.map((item, i) => (i === index ? { ...item, antwoord: event.target.value } : item)),
                    )
                  }
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => setFaqs((huidig) => huidig.filter((_, i) => i !== index))}
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Vraag verwijderen
                </Button>
              </li>
            ))}
          </ol>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setFaqs((huidig) => [...huidig, { vraag: "", antwoord: "" }])}
          >
            Vraag toevoegen
          </Button>
        </CardContent>
      </Card>

      {/* Foto's */}
      <Card className="mb-6">
        <CardContent className="space-y-4 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Foto's</h2>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" disabled={!existing} onClick={() => setKiezen(true)}>
                <ImagePlus className="mr-2 h-4 w-4" />
                Kies uit mediatheek of computer
              </Button>
              <Button type="button" size="sm" disabled={!existing || savePhotos.isPending} onClick={() => savePhotos.mutate()}>
                Foto's bewaren
              </Button>
            </div>
          </div>

          {!existing && (
            <p className="text-sm text-muted-foreground">
              Bewaar eerst de gegevens als concept; daarna kunt u foto's koppelen.
            </p>
          )}

          <ol className="space-y-3">
            {photos.map((photo, index) => (
              <li
                key={photo.mediaId}
                draggable
                onDragStart={() => (dragIndex.current = index)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => dropOn(index)}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3"
              >
                <img
                  src={photo.media.url}
                  alt={photo.media.alt}
                  width={96}
                  height={72}
                  className="h-16 w-24 rounded object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{photo.media.alt || "Beschrijving ontbreekt nog"}</p>
                  {photo.media.alt.trim() === "" && (
                    <p className="text-xs text-destructive">Beschrijving verplicht — vul die aan bij Foto's.</p>
                  )}
                </div>
                <label className="text-xs">
                  Rol
                  <select
                    aria-label={`Rol van foto ${index + 1}`}
                    value={photo.role}
                    onChange={(event) =>
                      setPhotos((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, role: event.target.value as ProjectMediaRole } : item,
                        ),
                      )
                    }
                    className="ml-2 h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {Object.entries(roleLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                {photo.role === "voor" && (
                  <label className="text-xs">
                    Hoort bij
                    <select
                      aria-label={`Na-foto bij foto ${index + 1}`}
                      value={photo.pairedMediaId ?? ""}
                      onChange={(event) =>
                        setPhotos((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, pairedMediaId: event.target.value || null } : item,
                          ),
                        )
                      }
                      className="ml-2 h-9 rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="">Kies de na-foto</option>
                      {naOpties.map((option) => (
                        <option key={option.mediaId} value={option.mediaId}>
                          {option.media.alt || option.media.filename || option.mediaId.slice(0, 8)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    aria-label={`Foto ${index + 1} naar boven`}
                    disabled={index === 0}
                    onClick={() => movePhoto(index, -1)}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    aria-label={`Foto ${index + 1} naar beneden`}
                    disabled={index === photos.length - 1}
                    onClick={() => movePhoto(index, 1)}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    aria-label={`Foto ${index + 1} loskoppelen`}
                    onClick={() => setPhotos((current) => current.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ol>

          <MediaDialog
            open={kiezen}
            onOpenChange={setKiezen}
            mode="multiple"
            uploadCollectie="realisaties"
            reedsGekozen={photos.map((photo) => photo.mediaId)}
            onSelect={(ids) => void addByIds(ids)}
          />
        </CardContent>
      </Card>

      {/* Quote */}
      <Card className="mb-6">
        <CardContent className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">Klantquote</h2>
          <div>
            <Label htmlFor="citaat">Citaat</Label>
            <Textarea
              id="citaat"
              rows={3}
              value={quote.citaat}
              onChange={(event) => setQuote({ ...quote, citaat: event.target.value })}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="quoteNaam">Naam of initialen</Label>
              <Input
                id="quoteNaam"
                value={quote.naam}
                onChange={(event) => setQuote({ ...quote, naam: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="quoteGemeente">Gemeente</Label>
              <Input
                id="quoteGemeente"
                value={quote.gemeente}
                onChange={(event) => setQuote({ ...quote, gemeente: event.target.value })}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={quote.toestemming}
              onChange={(event) =>
                setQuote({
                  ...quote,
                  toestemming: event.target.checked,
                  zichtbaar: event.target.checked ? quote.zichtbaar : false,
                  toestemmingOp: event.target.checked
                    ? quote.toestemmingOp || siteDay()
                    : "",
                })
              }
              className="h-4 w-4"
            />
            Toestemming gekregen van de klant
          </label>
          <div>
            <Label htmlFor="toestemmingOp">Datum van de toestemming</Label>
            <Input
              id="toestemmingOp"
              type="date"
              disabled={!quote.toestemming}
              value={quote.toestemmingOp}
              onChange={(event) => setQuote({ ...quote, toestemmingOp: event.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={!quote.toestemming}
              checked={quote.zichtbaar}
              onChange={(event) => setQuote({ ...quote, zichtbaar: event.target.checked })}
              className="h-4 w-4"
            />
            Toon deze quote op de website
          </label>
          <p className="text-xs text-muted-foreground">
            Zonder toestemming blijft de quote onzichtbaar; dat wordt ook in de databank afgedwongen.
          </p>
          <Button type="button" size="sm" disabled={!existing || storeQuote.isPending} onClick={() => storeQuote.mutate()}>
            Quote bewaren
          </Button>
        </CardContent>
      </Card>

      {existing && (
        <Button type="button" variant="outline" disabled={remove.isPending} onClick={() => remove.mutate()}>
          <Trash2 className="mr-2 h-4 w-4" /> Realisatie verwijderen
        </Button>
      )}
    </main>
  );
}
